import useAutocomplete, { AutocompleteInputChangeReason } from '@material-ui/lab/useAutocomplete';
import { clsx } from 'clsx';
import dayjs from 'dayjs';
import isEmpty from 'lodash/isEmpty';
import React, { useEffect, useId, useMemo, useRef, useState } from 'react';
import { DayPickerProps } from 'react-day-picker';
import { useIntl } from 'react-intl';
import { useDispatch, useSelector } from 'react-redux';

import { ESearchFilters } from '@/constants/searchFilters';
import { useBreakpoint } from '@/hooks/useBreakpoint';
import useComponentVisible from '@/hooks/useComponentVisible';
import { useIsLocalityPage } from '@/hooks/useIsLocalityPage';
import {
  ILocationSearchAutocompleteListOption,
  useLocationSearchAutocompleteOptions,
} from '@/hooks/useLocationSearchAutocompleteOptions';
import { useLocationSearchSuggestions } from '@/hooks/useLocationSearchSuggestions';
import useQueryParams from '@/hooks/useQueryParams';
import { IHeaderSearchFilterDates, IHeaderSearchFilterGuests } from '@/redux/modules/globalHeader';
import { applySearchFilter, unsetSearchFilter } from '@/redux/modules/search';
import { getSearchFormRVProfileFilter } from '@/redux/selectors/search/searchForm';
import { getSearchResults } from '@/redux/selectors/search/searchResults';
import { ERentalCategory } from '@/services/types/search/rentals/id';
import { IGroupOption, limitRecentSearches } from '@/utility/autoCompleteGroupedOptions';
import { getFocusableElements } from '@/utility/focusableElements';
import { formatDateFullRange } from '@/utility/format-date';
import { stripDiacritics } from '@/utility/stripDiacritics';
import {
  LocationGroupType,
  mapLocationLabelToGroupName,
  setSurfacedLocation,
} from '@/utility/surfacedLocation';

import { QUERYSTRING_DATE_FORMAT } from '../../SearchHeader';
import { EVENT_OPEN_SEARCH_DATES, EVENT_OPEN_SEARCH_RV_PROFILE } from '../events';
import DateContent from './DateContent/DateContent';
import DetailsContent from './DetailsContent/DetailsContent';
import GuestsContent from './GuestsContent/GuestsContent';
import LocationSearchContent from './LocationSearchContent';
import LocationSearchInput from './LocationSearchInput';
import RVProfileContent from './RVProfileContent/RVProfileContent';
import { DateTab, GuestsTab, RVProfileTab, SearchButton } from './SearchTabs';

enum SEARCH_TAB {
  DATES = 'DATES',
  DETAILS = 'DETAILS',
  LOCATION = 'LOCATION',
  RV_PROFILE = 'RV_PROFILE',
}

interface IDesktopNavbarSearchProps {
  handleRecentSearchNavigation?: (url: string) => void;
  onSubmit?: () => void;
  location?: { lat: number; lng: number };
  address?: string;
  guests?: IHeaderSearchFilterGuests;
  dates?: IHeaderSearchFilterDates;
  recentSearches?: ILocationSearchAutocompleteListOption[];
  maxRecentSearches: number;
  loading?: boolean;
  onChangeAddress?: (address?: string, addressOption?: any) => void;
  onChangeDates?: (address?: IHeaderSearchFilterDates) => void;
  onChangeGuests?: (guests?: IHeaderSearchFilterGuests) => void;
  onConfirmAddress?: (address?: any, skipSubmit?: boolean) => void;
  onConfirmDates?: (dates?: IHeaderSearchFilterDates) => void;
  onConfirmGuests?: (guests: IHeaderSearchFilterGuests) => void;
  popularDestinations?: ILocationSearchAutocompleteListOption[];
  alertStatus?: boolean;
  isPromoSearch?: boolean;
  isGuestOccupancyAvailable?: boolean;
  isSERP?: boolean;
  scrollToSearchFilter?: () => void;
  variant?: 'minimal' | 'enhanced';
  disabledDays?: DayPickerProps['disabled'];
  isCampground?: boolean;
  canShrink?: boolean;
  redesigned?: boolean;
}

const DesktopNavbarSearch: React.FC<IDesktopNavbarSearchProps> = ({
  onSubmit,
  location,
  address,
  dates,
  guests,
  recentSearches = [],
  maxRecentSearches,
  loading = false,
  onChangeAddress,
  onChangeDates,
  onChangeGuests,
  onConfirmAddress,
  onConfirmDates,
  onConfirmGuests,
  popularDestinations = [],
  alertStatus,
  handleRecentSearchNavigation,
  isPromoSearch,
  variant,
  isGuestOccupancyAvailable,
  isSERP,
  scrollToSearchFilter,
  disabledDays,
  isCampground = false,
  canShrink,
  redesigned,
}) => {
  const dispatch = useDispatch();
  const intl = useIntl();
  const { isMobile } = useBreakpoint();
  const searchResult = useSelector(getSearchResults);
  const { isLocalityPage } = useIsLocalityPage();

  // Only apply flexible layout when explicitly enabled AND the form is in-flow
  // (not absolute-positioned, which happens on home/listing pages).
  const isFlexLayout = !!canShrink && (!!isSERP || isLocalityPage);

  const isEnhanced = variant === 'enhanced';
  const isMinimal = variant === 'minimal';
  const hasVariant = isEnhanced || isMinimal;

  const { ref: formRef, isComponentVisible, setIsComponentVisible } = useComponentVisible(true);
  const hasConfirmedOnCloseRef = useRef(false);
  const panelRef = useRef<HTMLDivElement>(null);
  // The control that opened the panel, so focus can go back to it on close.
  const panelTriggerRef = useRef<HTMLElement | null>(null);
  const [selectedTab, setSelectedTab] = useState<SEARCH_TAB | null>(null);
  const [addressValue, setAddressValue] = useState(address || '');

  const queryParams = useQueryParams();
  const rentalType = queryParams?.[ESearchFilters.RENTAL_CATEGORY] || ERentalCategory.RV;
  const searchFormRVProfile = useSelector(getSearchFormRVProfileFilter);

  const suggestions = useLocationSearchSuggestions(
    selectedTab === SEARCH_TAB.LOCATION ? addressValue : '',
    location ? [location.lng, location.lat] : undefined,
  );

  const formattedDates = useMemo<string | null>(() => {
    if (!dates?.from || !dates?.to) {
      return null;
    }

    return formatDateFullRange(dates.from.toString(), dates.to.toString());
  }, [dates]);

  useEffect(() => {
    if (address !== addressValue) {
      setAddressValue(address || '');
    }
  }, [address, addressValue]);

  const { options, optionGroupLabels } = useLocationSearchAutocompleteOptions({
    shouldBuildGroupedOptions: false,
    suggestions,
    recentSearches,
    popularDestinations,
    includeRecentSearches: true,
  });

  const handleAddressChange = (
    e: React.ChangeEvent<Record<string, string>>,
    value: string,
    reason: AutocompleteInputChangeReason,
  ) => {
    if (!e) return;
    if (selectedTab !== SEARCH_TAB.LOCATION) {
      // Case where text is selected and the user swipes outside the input field.
      handleClickTab(SEARCH_TAB.LOCATION);
    }

    if (reason === 'input') {
      onChangeAddress?.(value);
      setAddressValue(value);
    }
    setSurfacedLocation(LocationGroupType.OTHER);
  };

  const handleAddressSelect = (_e: React.ChangeEvent<Record<string, string>>, option: any) => {
    if (!option) return;
    const { label, url, groupName, value } = option;

    if (url) {
      handleRecentSearchNavigation?.(url);
      return;
    }

    if (value?.name) {
      onChangeAddress?.(value.name, option);
      setAddressValue(value.name);
    } else if (value?.address) {
      onChangeAddress?.(value.address, option);
      setAddressValue(value.address);
    } else {
      onChangeAddress?.(label, option);
      setAddressValue(label);
    }

    const groupType = mapLocationLabelToGroupName(groupName);
    setSurfacedLocation(groupType);
    if (!dates && label) {
      setSelectedTab(SEARCH_TAB.DATES);
      onConfirmAddress?.(option, true);
    }
    if (dates && label) {
      setSelectedTab(null);
      onConfirmAddress?.(option);
    }
  };

  const handleChangeDate = (value?: IHeaderSearchFilterDates) => {
    if ((value?.from && value?.to) || isEmpty(value)) {
      onChangeDates?.(value);

      if (isMinimal) {
        setIsComponentVisible(false);
      } else if (value?.from && value?.to) {
        // When both dates are selected, automatically move focus to guests tab
        handleClickTab(SEARCH_TAB.DETAILS);
      }
    }
  };

  const handleOpen = () => {
    setSelectedTab(SEARCH_TAB.LOCATION);
  };

  // Own the autocomplete's id instead of letting MUI generate one: its
  // `unstable_useId` returns undefined on the first render, so the input's
  // aria-controls and the listbox's id are missing on the server-rendered
  // markup and only appear after hydration. `useId` is stable across both.
  const autocompleteId = useId();
  // The dropdown renders one panel at a time, so a single id is enough for the
  // tab buttons to point at whichever panel is currently open.
  const panelId = `${autocompleteId}-panel`;

  const { getInputProps, getInputLabelProps, getListboxProps, getOptionProps, groupedOptions } =
    useAutocomplete({
      id: autocompleteId,
      inputValue: addressValue,
      options,
      filterOptions: (autocompleteOptions, autocompleteState) => {
        const input = stripDiacritics(autocompleteState.inputValue.trim().toLowerCase());

        if (input.length <= 2) {
          return autocompleteOptions;
        }

        return autocompleteOptions.filter(autocompleteOption => {
          // Filter everything except suggestions
          if (autocompleteOption.groupName === optionGroupLabels.suggestions) {
            return autocompleteOption;
          }

          const candidate = stripDiacritics(
            autocompleteState.getOptionLabel(autocompleteOption).trim().toLowerCase(),
          );

          return candidate.indexOf(input) > -1;
        });
      },
      getOptionLabel: option => option.label,
      groupBy: option => option.groupName,
      onInputChange: handleAddressChange,
      onChange: handleAddressSelect,
      onOpen: handleOpen,
      freeSolo: true,
      open: selectedTab === SEARCH_TAB.LOCATION,
    });

  // The autocomplete's own ref to the input. The field and its popup form one group
  // for focus purposes, and telling whether focus is still inside that group needs
  // the input element itself.
  const { ref: locationInputRef } = getInputProps() as unknown as {
    ref: React.RefObject<HTMLInputElement>;
  };

  const handleClickTab = useMemo(() => {
    return (tab: SEARCH_TAB) => {
      setIsComponentVisible(true);
      setSelectedTab(currentSelectedTab => {
        return tab !== SEARCH_TAB.LOCATION && tab === currentSelectedTab ? null : tab;
      });
    };
  }, [setIsComponentVisible]);

  const datesTabIsOpened = isComponentVisible && selectedTab === SEARCH_TAB.DATES;
  const rvProfileTabIsOpened = isComponentVisible && selectedTab === SEARCH_TAB.RV_PROFILE;
  // Allow to open dates tab from outside
  // Because we close the tab on click outside, we have to delay the opening
  useEffect(() => {
    if (isMobile) return;

    const handleOnOpenSearchDates = () => {
      if (datesTabIsOpened) return;
      setTimeout(() => handleClickTab(SEARCH_TAB.DATES), 100);
    };

    const handleOnOpenSearchRVProfile = () => {
      if (rvProfileTabIsOpened) return;
      setTimeout(() => handleClickTab(SEARCH_TAB.RV_PROFILE), 100);
    };
    document.addEventListener(EVENT_OPEN_SEARCH_DATES, handleOnOpenSearchDates);
    document.addEventListener(EVENT_OPEN_SEARCH_RV_PROFILE, handleOnOpenSearchRVProfile);
    return () => {
      document.removeEventListener(EVENT_OPEN_SEARCH_DATES, handleOnOpenSearchDates);
      document.removeEventListener(EVENT_OPEN_SEARCH_RV_PROFILE, handleOnOpenSearchRVProfile);
    };
  }, [isMobile, datesTabIsOpened, rvProfileTabIsOpened, handleClickTab]);

  // Reset the close guard when the dropdown re-opens
  useEffect(() => {
    if (isComponentVisible) {
      hasConfirmedOnCloseRef.current = false;
    }
  }, [isComponentVisible]);

  // action when closing dropdown
  useEffect(() => {
    if (!isComponentVisible && !hasConfirmedOnCloseRef.current) {
      hasConfirmedOnCloseRef.current = true;
      if (selectedTab === SEARCH_TAB.DETAILS && guests) {
        onConfirmGuests?.(guests);
      }
      if (selectedTab === SEARCH_TAB.DATES && dates) {
        onConfirmDates?.(dates);
      }
      if (selectedTab === SEARCH_TAB.RV_PROFILE) {
        const filterSubTypes = searchFormRVProfile[ESearchFilters.CAMPSITE_SUPPORTED_SUBTYPES];
        const filterSlideouts = searchFormRVProfile[ESearchFilters.CAMPSITE_SUPPORTED_SLIDEOUTS];
        const filterLength = searchFormRVProfile[ESearchFilters.CAMPSITE_SUPPORTED_LENGTH];

        const filterResults: Record<string, string | undefined> = {};

        if (Array.isArray(filterSubTypes) && filterSubTypes.length > 0) {
          filterResults[ESearchFilters.CAMPSITE_SUPPORTED_SUBTYPES] = filterSubTypes.join(',');
        }

        if (filterLength) {
          filterResults[ESearchFilters.CAMPSITE_SUPPORTED_LENGTH] = filterLength.toString();
        }

        if (Array.isArray(filterSlideouts) && filterSlideouts.length > 0) {
          filterResults[ESearchFilters.CAMPSITE_SUPPORTED_SLIDEOUTS] = filterSlideouts.join(',');
        }

        const hasFilters = Object.keys(filterResults).length > 0;

        if (hasFilters) {
          dispatch(applySearchFilter(filterResults, false, false, false, undefined, false));
        } else {
          dispatch(
            unsetSearchFilter([
              ESearchFilters.CAMPSITE_SUPPORTED_SUBTYPES,
              ESearchFilters.CAMPSITE_SUPPORTED_LENGTH,
              ESearchFilters.CAMPSITE_SUPPORTED_SLIDEOUTS,
            ]),
          );
        }
      }

      setSelectedTab(null);
    }
  }, [
    isComponentVisible,
    guests,
    onConfirmGuests,
    onConfirmDates,
    dates,
    selectedTab,
    searchFormRVProfile,
    dispatch,
  ]);

  // we may hide some recent searches but require all options to calculate the index into the
  // results for getting option props (onClick handler)
  const allGroupOptions = groupedOptions as unknown as IGroupOption[];
  const groupOptions = limitRecentSearches(
    groupedOptions as unknown as IGroupOption[],
    optionGroupLabels.recentSearches,
    maxRecentSearches,
  );

  const handleSubmit = (e: React.BaseSyntheticEvent) => {
    e.preventDefault();
    if (
      !address &&
      !hasVariant &&
      (!isPromoSearch || (isPromoSearch && !dates?.from)) &&
      dates?.from &&
      dates?.to &&
      searchResult.meta
    ) {
      const { lat, lng, city, state, country_name } = searchResult.meta;

      const searcherGeoLocation = [city, state, country_name].filter(Boolean).join(', ');

      if (searcherGeoLocation) {
        onChangeAddress?.(searcherGeoLocation, null);
        handleChangeDate?.(dates);
        setAddressValue(searcherGeoLocation);
        setSelectedTab(null);
        dispatch(
          applySearchFilter(
            {
              [ESearchFilters.ADDRESS]: searcherGeoLocation,
              [ESearchFilters.DELIVERY_DETAILS]: encodeURIComponent(
                JSON.stringify({
                  country: country_name,
                  state,
                  city,
                }),
              ),
              [ESearchFilters.DELIVERY_ADDRESS]: searcherGeoLocation,
              [ESearchFilters.DELIVERY_QUERY]: searcherGeoLocation,
              [ESearchFilters.DELIVERY_CENTER]: JSON.stringify([lng, lat]),
              [ESearchFilters.DATE_FROM]: dates?.from
                ? dayjs(dates.from).format(QUERYSTRING_DATE_FORMAT)
                : undefined,
              [ESearchFilters.DATE_TO]: dates?.to
                ? dayjs(dates.to).format(QUERYSTRING_DATE_FORMAT)
                : undefined,
              [ESearchFilters.FLEXIBLE_DAYS]: dates?.flexible_days
                ? String(dates.flexible_days)
                : undefined,
            },
            false,
            false,
            false,
            undefined,
            false,
          ),
        );
      }
    } else if (!address && !hasVariant && (!isPromoSearch || (isPromoSearch && !dates?.from))) {
      const inputEl = getInputProps() as any;
      inputEl.ref.current.focus();
      inputEl.onFocus();
    } else if (hasVariant && !dates?.from) {
      handleClickTab(SEARCH_TAB.DATES);
    } else {
      onSubmit?.();
      setSelectedTab(null);
      if (isEnhanced) {
        scrollToSearchFilter?.();
      }
    }
  };

  // No stray padding: this string is now the tab's accessible name as well as its
  // visible text.
  const dateTabText = formattedDates
    ? `${formattedDates}${dates?.flexible_days ? ` (±${dates.flexible_days})` : ''}`
    : '';

  // Only claim the combobox is expanded once the listbox is actually rendered.
  const isLocationListboxOpen = selectedTab === SEARCH_TAB.LOCATION && groupOptions.length > 0;

  const getLocationOptions = () =>
    Array.from(panelRef.current?.querySelectorAll<HTMLElement>('[role="option"]') ?? []);

  // The field's own controls, in order: the input, then the clear button once there
  // is something to clear.
  const getLocationFieldControls = () => {
    const field = locationInputRef.current?.closest<HTMLElement>('[data-location-field]');

    return field ? getFocusableElements(field) : [];
  };

  // Keyboard handling for the location field as a group — the input and its clear
  // button — which is why it is attached to the field wrapper rather than the input:
  // Bonfire's Close takes no key props of its own.
  const handleLocationFieldKeyDown = (event: KeyboardEvent) => {
    if (selectedTab !== SEARCH_TAB.LOCATION) return;

    const optionElements = getLocationOptions();
    if (optionElements.length === 0) return;

    const target = event.target as HTMLElement;
    const lastOption = optionElements[optionElements.length - 1];

    // The arrow keys are how a combobox's list is entered, and the role the input
    // advertises promises them. Home and End reach the ends, as in any listbox.
    if (target === locationInputRef.current) {
      if (event.key === 'ArrowDown' || event.key === 'Home') {
        event.preventDefault();
        optionElements[0]?.focus();
        return;
      }

      if (event.key === 'ArrowUp' || event.key === 'End') {
        event.preventDefault();
        lastOption?.focus();
        return;
      }
    }

    if (event.key !== 'Tab' || event.shiftKey) return;

    // Tab reaches the suggestions only once it has run out of controls in the field
    // itself, so the clear button keeps its place in the order instead of being
    // jumped over.
    const fieldControls = getLocationFieldControls();
    if (fieldControls[fieldControls.length - 1] !== target) return;

    event.preventDefault();
    optionElements[0]?.focus();
  };

  // The controls of the search bar itself, in order, with the dropdown's contents
  // left out. Tab order inside the widget is read off this rather than off the
  // document, because the dropdown is rendered after the whole field row.
  const getSearchBarControls = () => {
    const form = formRef.current;
    if (!form) return [];

    return getFocusableElements(form).filter(element => !panelRef.current?.contains(element));
  };

  // The first search-bar control that is not part of the location field — the dates
  // tab.
  const getControlAfterLocationField = () =>
    getSearchBarControls().find(element => !element.closest('[data-location-field]'));

  // Tab off either end of the list continues the search bar's own order rather than
  // the document's. The popup is rendered after the whole field row, so letting Tab
  // fall through would leave the field, skip the dates and guests tabs and land
  // somewhere past the search bar altogether — backwards off the front of the list
  // would skip the field itself the same way.
  const handleLocationOptionsKeyDown = (event: React.KeyboardEvent<HTMLDivElement>) => {
    const optionElements = getLocationOptions();
    if (optionElements.length === 0) return;

    // Arrows walk the list. Off the top, focus returns to the field the list belongs
    // to; off the bottom it stays put, rather than wrapping round to the start.
    const focusedIndex = optionElements.indexOf(event.target as HTMLElement);
    if (focusedIndex !== -1) {
      const arrowTarget: Record<string, HTMLElement | undefined> = {
        ArrowDown: optionElements[Math.min(focusedIndex + 1, optionElements.length - 1)],
        ArrowUp:
          focusedIndex === 0
            ? (getLocationFieldControls().at(-1) ?? locationInputRef.current ?? undefined)
            : optionElements[focusedIndex - 1],
        Home: optionElements[0],
        End: optionElements[optionElements.length - 1],
      };

      if (event.key in arrowTarget) {
        event.preventDefault();
        arrowTarget[event.key]?.focus();
        return;
      }
    }

    if (event.key !== 'Tab') return;

    const boundary = event.shiftKey ? optionElements[0] : optionElements[optionElements.length - 1];
    if (event.target !== boundary) return;

    const nextControl = event.shiftKey
      ? (getLocationFieldControls().at(-1) ?? locationInputRef.current)
      : getControlAfterLocationField();
    if (!nextControl) return;

    event.preventDefault();
    nextControl.focus();
  };

  // Closing the popup when focus leaves the field's group. Moving between the input
  // and its suggestions stays inside the group, so only a move out of both closes
  // it — otherwise the list hangs over the page while focus is already on the dates
  // tab. A null `relatedTarget` (focus going nowhere) counts as leaving.
  useEffect(() => {
    if (selectedTab !== SEARCH_TAB.LOCATION) return;

    const handleFocusOut = (event: FocusEvent) => {
      const nextFocused = event.relatedTarget as HTMLElement | null;
      const stillInGroup =
        !!nextFocused &&
        (!!nextFocused.closest('[data-location-field]') ||
          !!nextFocused.closest('[role="listbox"]'));

      if (stillInGroup) return;

      setSelectedTab(tab => (tab === SEARCH_TAB.LOCATION ? null : tab));
    };

    document.addEventListener('focusout', handleFocusOut);
    return () => document.removeEventListener('focusout', handleFocusOut);
  }, [selectedTab]);

  // The same rule for the panels: Tab off the end of an open panel goes to the tab
  // after the one that opened it, and Shift+Tab off the front goes back to that tab,
  // instead of falling through to whatever the markup happens to put next. Leaving
  // closes the panel the way Escape and a click outside do, so the values the user
  // set are confirmed rather than dropped.
  const handlePanelKeyDown = (event: React.KeyboardEvent<HTMLDivElement>) => {
    const panel = panelRef.current;
    if (event.key !== 'Tab' || !panel) return;

    const controls = getFocusableElements(panel);
    const boundary = event.shiftKey ? controls[0] : controls[controls.length - 1];
    if (!boundary || event.target !== boundary) return;

    const trigger = panelTriggerRef.current;
    if (!trigger) return;

    const barControls = getSearchBarControls();
    const nextControl = event.shiftKey ? trigger : barControls[barControls.indexOf(trigger) + 1];
    if (!nextControl) return;

    event.preventDefault();
    // Move focus before closing: the close handler only reclaims focus for the
    // trigger when closing dropped it, and here it has a destination already.
    nextControl.focus();
    setIsComponentVisible(false);
  };

  // The dropdown hosts two different widgets: the location combobox, whose popup
  // belongs to the input, and the tab panels, which are dialogs opened by a button.
  const isPanelTabOpen =
    selectedTab === SEARCH_TAB.DATES ||
    selectedTab === SEARCH_TAB.DETAILS ||
    selectedTab === SEARCH_TAB.RV_PROFILE;

  const shouldOpenContentTab = isPanelTabOpen || isLocationListboxOpen;

  const panelLabel = useMemo(() => {
    switch (selectedTab) {
      case SEARCH_TAB.DATES:
        return intl.formatMessage({
          defaultMessage: 'Dates',
          id: 'roLYt/',
          description: 'Global Header > Search > Accessible name of the dates panel',
        });
      case SEARCH_TAB.DETAILS:
        return intl.formatMessage({
          defaultMessage: 'Guests',
          id: 'Gtes49',
          description: 'Global Header > Search > Accessible name of the guests panel',
        });
      case SEARCH_TAB.RV_PROFILE:
        return intl.formatMessage({
          defaultMessage: 'RV profile',
          id: 'MGeXr5',
          description: 'Global Header > Search > Accessible name of the RV profile panel',
        });
      default:
        return undefined;
    }
  }, [intl, selectedTab]);

  // Focus handling for the panels. The header menu gets this from FocusScope, but
  // FocusScope only autofocuses on mount, and mounting it per panel would unmount
  // the panels on every tab switch — DateContent keeps a half-picked range in local
  // state, so that would throw the pending start date away. Its `contain` is worse
  // here: the panel sits inside the search form next to the location input, and a
  // contained scope synchronously yanks focus back out of that input when it is
  // clicked while a panel is open. So the two parts we need are done by hand.
  useEffect(() => {
    if (isPanelTabOpen) {
      const opener = document.activeElement as HTMLElement | null;
      // Whatever was focused when the panel opened is the control to hand focus
      // back to — the tab button, or the newly clicked tab when switching panels.
      // Panels opened programmatically (the EVENT_OPEN_SEARCH_* events) have no
      // opener, so nothing is recorded and nothing is restored.
      if (opener && opener !== document.body && !panelRef.current?.contains(opener)) {
        panelTriggerRef.current = opener;
      }
      // Focus goes to the first control the panel actually shows, not to the panel
      // box — the contents are what the user opened it for, and landing on them is
      // what makes Tab continue through the panel instead of past the search bar.
      // The panels that are off screen stay mounted, so `getFocusableElements`
      // skips them by their `hidden` attribute. Re-runs when moving between panels,
      // so switching tabs lands on the new panel's first control.
      const panel = panelRef.current;
      const [firstControl] = panel ? getFocusableElements(panel) : [];
      (firstControl ?? panel)?.focus();
      return;
    }

    const trigger = panelTriggerRef.current;
    panelTriggerRef.current = null;

    // Only reclaim focus when closing the panel dropped it — Escape, or anything
    // else that unmounts the focused node. If focus already moved somewhere real
    // (another tab, the location input, a Tab out of the widget) it stays there.
    const activeElement = document.activeElement;
    if (trigger && (!activeElement || activeElement === document.body)) {
      trigger.focus();
    }
  }, [isPanelTabOpen, selectedTab]);

  return (
    <>
      <form
        className={clsx({
          ['absolute top-3']: !isPromoSearch && !hasVariant && !isSERP && !isLocalityPage,
          ['w-full']: isFlexLayout,
        })}
        onSubmit={handleSubmit}
        ref={formRef}>
        <div
          className={clsx(
            'bg-white flex items-center',
            isFlexLayout ? 'w-full' : 'w-fit',
            isEnhanced
              ? 'rounded-full pl-3 h-[100px] flex items-center justify-center'
              : 'rounded-full h-[48px]',
            {
              'border border-solid border-neutral-20': !redesigned && !isSERP && !isLocalityPage,
              'shadow-[0_0_0_1px_theme(colors.gray.310)]': redesigned,
            },
          )}>
          {!hasVariant && (
            <LocationSearchInput
              getInputProps={getInputProps}
              getInputLabelProps={getInputLabelProps}
              onFieldKeyDown={handleLocationFieldKeyDown}
              onFieldFocus={() => handleClickTab(SEARCH_TAB.LOCATION)}
              expanded={isLocationListboxOpen}
              alertStatus={alertStatus}
              flexible={isFlexLayout}
              redesigned={redesigned}
            />
          )}
          <DateTab
            onClick={() => handleClickTab(SEARCH_TAB.DATES)}
            selectedDates={dateTabText}
            variant={variant}
            redesigned={redesigned}
            isOpen={selectedTab === SEARCH_TAB.DATES}
            panelId={panelId}
          />
          <GuestsTab
            onClick={() => handleClickTab(SEARCH_TAB.DETAILS)}
            guests={guests}
            variant={variant}
            isGuestOccupancyAvailable={isGuestOccupancyAvailable}
            redesigned={redesigned}
            isOpen={selectedTab === SEARCH_TAB.DETAILS}
            panelId={panelId}
          />
          {isCampground && isMinimal && (
            <RVProfileTab
              onClick={() => handleClickTab(SEARCH_TAB.RV_PROFILE)}
              variant={variant}
              redesigned={redesigned}
              isOpen={selectedTab === SEARCH_TAB.RV_PROFILE}
              panelId={panelId}
              hasValue={
                !!(
                  searchFormRVProfile[ESearchFilters.CAMPSITE_SUPPORTED_SUBTYPES] ||
                  searchFormRVProfile[ESearchFilters.CAMPSITE_SUPPORTED_LENGTH] ||
                  searchFormRVProfile[ESearchFilters.CAMPSITE_SUPPORTED_SLIDEOUTS]
                )
              }
            />
          )}
          <SearchButton loading={loading} variant={variant} />
        </div>

        {shouldOpenContentTab && (
          <div
            data-tab="dropdown_wrapper"
            className={clsx('relative', {
              ['z-10 max-w-30']: isPromoSearch || hasVariant,
            })}>
            <div
              ref={panelRef}
              id={panelId}
              data-tab={selectedTab}
              // A panel is a dialog opened by a tab button; the location popup is
              // the combobox's listbox and is labelled through the input instead.
              role={isPanelTabOpen ? 'dialog' : undefined}
              aria-label={isPanelTabOpen ? panelLabel : undefined}
              tabIndex={isPanelTabOpen ? -1 : undefined}
              onKeyDown={isPanelTabOpen ? handlePanelKeyDown : undefined}
              className={clsx(
                'absolute top-0 overflow-hidden bg-white mt-2 rounded-2xl shadow-100 p-4',
                {
                  'w-fit': rentalType === ERentalCategory.RV,
                  'ml-[166px]':
                    selectedTab === SEARCH_TAB.DETAILS &&
                    !hasVariant &&
                    rentalType === ERentalCategory.RV,
                  'right-0': isMinimal,
                },
              )}>
              <div className="overflow-auto max-h-[calc(100vh-7.5em)]">
                {!hasVariant && (
                  <LocationSearchContent
                    open={selectedTab === SEARCH_TAB.LOCATION}
                    groupOptions={groupOptions}
                    allGroupOptions={allGroupOptions}
                    getListboxProps={getListboxProps}
                    getOptionProps={getOptionProps}
                    onOptionKeyDown={handleLocationOptionsKeyDown}
                  />
                )}
                <DateContent
                  dates={dates}
                  onChangeDates={handleChangeDate}
                  open={selectedTab === SEARCH_TAB.DATES}
                  disabledDays={disabledDays}
                  allowClear={!isCampground}
                  maximumDays={isCampground ? 28 : undefined}
                />
                {hasVariant || isGuestOccupancyAvailable ? (
                  <DetailsContent
                    guests={guests}
                    onChangeGuests={onChangeGuests}
                    onConfirmGuests={onConfirmGuests}
                    open={selectedTab === SEARCH_TAB.DETAILS}
                    isGuestOccupancyAvailable={isGuestOccupancyAvailable}
                    isCampground={isCampground}
                  />
                ) : (
                  <GuestsContent
                    guests={guests}
                    onChangeGuests={onChangeGuests}
                    open={selectedTab === SEARCH_TAB.DETAILS}
                    isCampground={isCampground}
                  />
                )}
                <RVProfileContent open={selectedTab === SEARCH_TAB.RV_PROFILE} />
              </div>
            </div>
          </div>
        )}
      </form>
    </>
  );
};

export default DesktopNavbarSearch;
